import React, {useState, useEffect,useRef} from "react";
import {useParams, Link} from 'react-router-dom';
import moment from 'moment';
import {Table, Select, DatePicker, Breadcrumb, Skeleton, Typography,Tooltip} from 'antd';
import { HomeOutlined, ContactsOutlined, CloseCircleOutlined } from '@ant-design/icons';
import * as Styled from './style.js';
import { getData } from '../../config/api-connect';
import SummaryModal from '../../components/summaryModal'

const { Option } = Select;

const AttendanceSummary = () => {
  const { venue_id, game_level } = useParams();
  const local_selectedVenueId = venue_id;
  const local_game_level = game_level;
  const local_month = JSON.parse(localStorage.getItem('month'));
  const local_year = JSON.parse(localStorage.getItem('year'));
  const local_game_type = JSON.parse(localStorage.getItem('gameType'));
  const [modalVisible, setModalVisible] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)
  const [sessionData, setSessionData] = useState(null)
  const [loading, setLoading] = useState(true);
  const [selectedVenueId, setSelectedVenueId] = useState(local_selectedVenueId);
  const [batchesData, setBatchesData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [month, setMonth] = useState(local_month);
  const [year, setYear] = useState(local_year);
  const [venueName, setVenueName] = useState('');
  const [gameType, setGameType] = useState(local_game_type);
  const [gameLevel, setGameLevel] = useState(local_game_level);
  const tableRef = useRef(null)
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    localStorage.setItem('month', JSON.stringify(month));
    localStorage.setItem('year', JSON.stringify(year));
    getBatchDetails();
  }, [selectedVenueId, month, year]);

  useEffect(() => {
    if (!loading && batchesData.length > 0 && columns.length > 0) {
      setTimeout(() => scrollToLastColumn(), 1000);
    }
  }, [loading, batchesData, columns]);

  const smoothHorizontalScroll = (element, target, duration = 300) => {
    const start = element.scrollLeft;
    const change = target - start;
    const startTime = performance.now();

    const animate = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      const ease = t * (2 - t); // ease-out
      element.scrollLeft = start + change * ease;

      if (t < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  };

  const scrollToLastColumn = () => {
    const body = document.querySelector(".ant-table-content");
    if (!body) return;
    smoothHorizontalScroll(body, body.scrollWidth);
  };

  const getBatchDetails = () => {
    setLoading(true);
    hasScrolledRef.current = false;
    
    let url = `/business/get_day_wise_attendance_summary/?venue_id=${selectedVenueId}&game_type_id=${gameType}&month=${month}&year=${year}`;
    
    if(gameLevel && gameLevel !== 'All'){
      url += `&game_level=${gameLevel}`;
    }

    getData(url)
      .then(response => {
        if(response.data && response.data.length > 0) {
          buildColumns(response.headers);
          setBatchesData(response.data);
          
          if(response.data[0] && response.data[0].venue) {
            setVenueName(response.data[0].venue);
          }
        } else {
          setColumns([]);
          setBatchesData([]);
        }
        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
        console.log(error.message);
      });
  };

  const fetchSessionDetails = async (sessionId,batchId) => {
    setModalLoading(true);
    try {
      const response = await getData(
        `/business/get_session_data/?session_id=${sessionId}`
      );
      setSessionData({...response.result,batch_id: batchId});
    } catch (error) {
      console.error('Error fetching session details:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleOpenModal = (sessionId,batchId) => {
    if (!sessionId || !batchId) return;
    setModalVisible(true);

    fetchSessionDetails(sessionId,batchId);
  };

  const buildColumns = (headers) => {
    if (!headers) return;

    const cols = [];

    for (let i = 0; i < headers.length; i++) {

      let dataIndex = '';
      let title = headers[i];
      let subtitle = null;

      if (title === 'Skill Level' || title === 'Coach') {
        continue;
      }

      if (i < 5) {
        const keyMap = {
          'Batch': 'timing',      
          'Age Group': 'batch_info', 
          'Customers': 'customers'
        };

        dataIndex = keyMap[title] || title.toLowerCase().replace(/ /g, '_');

        if (title === 'Batch') title = 'Timing';
        if (title === 'Age Group') title = 'Batch';


      } else {
        const [dayName, dateStr] = headers[i].split(' ');

        let dateObj = moment(dateStr, "DD/MM/YYYY")
        title = dateObj.format("DD")
        subtitle = dateObj.format("ddd")
        dataIndex = headers[i]; 
      }

      const column = {
        title: subtitle ? (
          <div style={{ textAlign: 'center' }}>
            <div>{title}</div>
            <div>{subtitle}</div>
          </div>
        ) : (
          <div style={{ textAlign: i === 0 ? 'left' : 'center', fontWeight: 'bold' }}>{title}</div>
        ),
        onHeaderCell: (record, rowIndex) => {
          return {
            style: {
              fontSize: i >= 5 ? '12px' : '',
              padding: '6px',
            },
          };
        }, 
        onCell: (record) => ({
          style: {
            border: i >= 5 ? '1px solid #f0f0f0' : '',
            fontSize: i >= 5 ? '12px' : '13px',
            padding: '6px 4px',
            background:
              typeof record[dataIndex] === 'object' &&
              record[dataIndex]?.attendance === 'Cancelled'
                ? '#eee'
                : ''
          }
        }),
        dataIndex,
        key: dataIndex,
        align: i === 0 ? 'left' : 'center',
        width: i < 5 ? 110 : 35,
        fixed: i < 5 ? 'left' : ''
      };

      if (dataIndex === 'batch_info') {
        column.render = (_, record) => (
          <div>
            <div>{record.age_group} {record.game_level}</div>
            <div>{record.buddy_names}</div>
          </div>
        );
      }
      else if(dataIndex === 'customers') {
        column.width = 80
      }
      else if (dataIndex === 'timing') {
        column.width = 190;
        column.render = (value, record) => {
          const { start_time, end_time, days, multi_timings,id } = record;
          return (
            <a className='venueTitle' target="_blank" href={`https://cm.sporthood.in/#/fixtures/${id}/session`}>
              {
                multi_timings?.length > 0 ?
                  multi_timings.map((x) => (
                    <div key={x.day} style={{ display: 'flex' }}>
                      <span style={{ flex: 1 }}>{x.day + ' : '}</span>
                      <span style={{ flex: 4 }}>{x.start_time + ' - ' + x.end_time}</span>
                    </div>
                  )) :
                  <div>
                    <div>{days}</div>
                    <div>{start_time + ' - ' + end_time}</div>
                  </div>
              }
            </a>
          );
        };
      }
      else if (subtitle) {
        column.render = (value, record) => {
          if (!value || typeof value !== "object") return <div></div>;

          const { attendance, session_id } = value;

          const displayValue =
            attendance === "Cancelled"
              ? <CloseCircleOutlined />
              : attendance;

          return (
            <Tooltip title={attendance === "Cancelled" ? "Session Cancelled" : "Click To View Session Details"}>
              <div
                style={{
                  cursor: "pointer",
                  color: attendance === "Cancelled" ? "red" : ""
                }}
                onClick={() => {
                  if (attendance !== "Cancelled") {
                    handleOpenModal(session_id,record.id);
                  }
                }}
              >
                {displayValue}
              </div>
            </Tooltip>
          );
        };
      }

      cols.push(column);
    }

    setColumns(cols);
  };


  const onChangeYear = (date) => {
    if(date) {
      setYear(date.format('YYYY'));
    }
  };

  const onChangeMonth = (value) => {
    setMonth(value);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSessionData(null);
  };

  return(
    <Styled.MainContainer>

      <Styled.TopDiv>    
        <Breadcrumb style={{padding:'10px'}} >
          <Breadcrumb.Item>
            <Link to="/">
              <HomeOutlined />
            </Link>
          </Breadcrumb.Item>

          <Breadcrumb.Item>
              <ContactsOutlined />
              <span style={{paddingLeft:'5px'}}>Batches</span>
          </Breadcrumb.Item>

          <Breadcrumb.Item>
            <Link to="/batches/attendance/?page=summary">
              <span style={{paddingLeft:'5px'}}>Attendance</span>
            </Link>
          </Breadcrumb.Item>

          <Breadcrumb.Item>{venueName}</Breadcrumb.Item>
        </Breadcrumb>

        <div style={{display: 'flex'}}>
          <Styled.Selector>
            <Select
              className='mainSelector'
              style={{width: 150}}
              onChange={onChangeMonth}
              value={month}
              bordered={false}
            >
              <Option value="1">January</Option>
              <Option value="2">February</Option>
              <Option value="3">March</Option>
              <Option value="4">April</Option>
              <Option value="5">May</Option>
              <Option value="6">June</Option>
              <Option value="7">July</Option>
              <Option value="8">August</Option>
              <Option value="9">September</Option>
              <Option value="10">October</Option>
              <Option value="11">November</Option>
              <Option value="12">December</Option>
            </Select>
          </Styled.Selector>

          <Styled.Selector>
            <div className='mainSelector'>
              <DatePicker 
                value={moment(year, 'YYYY')}
                onChange={onChangeYear}
                bordered={false}
                picker="year"
              />
            </div> 
          </Styled.Selector>
        </div>
      </Styled.TopDiv>

      <Styled.Header>    
        <span></span>      
        <span>Attendance Summary {gameType == 8 ? '- Football Academy' : gameType == 10 ? '- Badminton Academy' :gameType == 65 ? '- Swimming Academy' : null} {gameLevel && gameLevel != 'All' ? `- ${gameLevel}` : ''}</span>
        <span></span>      
      </Styled.Header>
      <Typography.Text type="primary" style={{ fontSize: 11, textAlign:'right' }}>
        * <b>Customers</b> count includes <b>active</b> and <b>payment due</b> customers
      </Typography.Text>
      {loading ? (
        <Skeleton active paragraph={{ rows: 7 }} title={false} />
      ) : (
        <div>
          <Table
            ref={tableRef}
            className="drill-down-table table_container"
            rowKey={(record, index) => `${record.batch}_${index}`}
            columns={columns}
            dataSource={batchesData}
            pagination={{
              pageSize: 50,
              showSizeChanger: false,
            }}
            scroll={{ x: 'max-content' }}
          />
          <SummaryModal
            visible={modalVisible}
            onClose={handleCloseModal}
            loading={modalLoading}
            sessionData={sessionData}
          />
        </div>
      )}

    </Styled.MainContainer>
  );
};

export default AttendanceSummary;